import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaEnvelope,
  FaHandHoldingHeart,
  FaLock,
  FaShieldAlt,
  FaExternalLinkAlt
} from 'react-icons/fa';
import '../styles/Donate.css';
import environment from '../config/environment';

const CONTACT_EMAIL = 'Donate@CauseandImpact.org';
const SALESFORCE_DONATION_BASE =
environment.SF_DONATION_BASE;

const GENERAL_CAUSE_FALLBACK = {
  id: 'cause-and-impact',
  slug: 'cause-and-impact',
  name: 'Cause and Impact',
  projectName: 'Cause and Impact',
  shortDescription:
    'Your donation helps support Cause and Impact’s fiscal sponsorship infrastructure, compliance oversight, donor receipting, and operational capacity.',
  primaryColor: '#1761d1',
  secondaryColor: '#26914f',
  backgroundColor: '#ffffff',
  image: ''
};

const generalDonationContent = {
  title: 'Support Cause and Impact',
  description:
    'Your donation helps sustain the infrastructure, stewardship, and operational support that allows charitable projects to function under professional fiscal sponsorship.',
  supportPoints: [
    'Fiscal sponsorship administration',
    'Donation processing and donor receipting',
    'Compliance and financial oversight',
    'Operational support for charitable projects'
  ]
};

const formatAmountInput = (value) => {
  if (!value) return '';
  const cleanValue = value.toString().replace(/[^\d.]/g, '');
  const parts = cleanValue.split('.');

  if (parts.length > 2) {
    return `${parts[0]}.${parts[1]}`;
  }

  if (parts[1]?.length > 2) {
    return `${parts[0]}.${parts[1].slice(0, 2)}`;
  }

  return cleanValue;
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const Donate = () => {
  const { causeId } = useParams();
  const { apiUrl } = environment();

  const [cause, setCause] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [coverFees, setCoverFees] = useState(true);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const fetchCause = async () => {
      if (!causeId || causeId === 'cause-and-impact') {
        setCause(GENERAL_CAUSE_FALLBACK);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setPageError(null);

        const response = await fetch(
          `${apiUrl}/api/salesforce-public-causes/${encodeURIComponent(causeId)}`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load cause (${response.status})`);
        }

        const data = await response.json();

        if (data?.status === 'success' && data?.data?.cause) {
          setCause(data.data.cause);
          return;
        }

        throw new Error('Cause data not found.');
      } catch (error) {
        console.error('Donate page Salesforce cause fetch failed:', error);
        setPageError('Unable to load cause details.');
      } finally {
        setLoading(false);
      }
    };

    fetchCause();
  }, [causeId, apiUrl]);

  const isGeneralDonation =
    !causeId ||
    causeId === 'cause-and-impact' ||
    cause?.slug === 'cause-and-impact' ||
    cause?.name === 'Cause and Impact';

  const suggestedAmounts = useMemo(() => [25, 50, 100, 250, 500], []);

  const donationAmount = Number(selectedAmount || customAmount || 0);
  const adminFee = coverFees ? Math.round(donationAmount * 0.05 * 100) / 100 : 0;
  const totalAmount = Math.round((donationAmount + adminFee) * 100) / 100;
  const isAmountValid = donationAmount > 0;

  const pageTitle = isGeneralDonation
    ? generalDonationContent.title
    : `Support ${cause?.name || cause?.projectName}`;

  const pageDescription = isGeneralDonation
    ? generalDonationContent.description
    : cause?.shortDescription ||
      cause?.description ||
      'Make a secure donation to support this fiscally sponsored project through Cause and Impact.';

  const donateTargetUrl = isGeneralDonation
    ? `${SALESFORCE_DONATION_BASE}cause-and-impact`
    : `${SALESFORCE_DONATION_BASE}${encodeURIComponent(cause?.slug || '')}`;

  const handlePresetAmountClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (event) => {
    const value = formatAmountInput(event.target.value);

    if (value === '' || (!Number.isNaN(Number(value)) && Number(value) >= 0)) {
      setCustomAmount(value);
      setSelectedAmount('');
    }
  };

  const goNext = () => {
    if (step === 1) {
      setStep(2);
      return;
    }

    if (step === 2 && isAmountValid) {
      setStep(3);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  const handleContinueToSecureDonation = () => {
    const url = new URL(donateTargetUrl);
    if (isAmountValid) {
      url.searchParams.set('amount', String(donationAmount));
    }
    url.searchParams.set('coverFees', String(coverFees));
    window.location.href = url.toString();
  };

  if (loading) {
    return (
      <div className="donate-wrapper">
        <div className="donate-container">
          <div className="loading-state">Loading donation page...</div>
        </div>
      </div>
    );
  }

  if (pageError || !cause) {
    return (
      <div className="donate-wrapper">
        <div className="donate-container">
          <div className="error-state">
            <h2>Donation page unavailable</h2>
            <p>The cause you&apos;re looking for could not be loaded.</p>
            <Link to="/donate" className="back-link">
              Return to Causes
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <section className="donate-step-panel">
          <div className="donate-header donate-header--compact">
            {cause.image && !isGeneralDonation && (
              <img src={cause.image} alt={cause.name || cause.projectName} className="cause-logo" />
            )}

            <div className="donate-eyebrow">Secure online giving</div>
            <h1>{pageTitle}</h1>
            <p className="impact-statement">{pageDescription}</p>

            <div className="donate-trust-row">
              <div className="trust-chip">
                <FaLock />
                <span>Secure checkout</span>
              </div>
              <div className="trust-chip">
                <FaShieldAlt />
                <span>Salesforce + Linvio powered</span>
              </div>
              <div className="trust-chip">
                <FaHandHoldingHeart />
                <span>Tax-deductible where allowed by law</span>
              </div>
            </div>

            {isGeneralDonation && (
              <div className="impact-points">
                <h3>Your support helps make possible:</h3>
                <ul>
                  {generalDonationContent.supportPoints.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      );
    }

    if (step === 2) {
      return (
        <section className="donate-step-panel">
          <div className="amount-selection">
            <h2>Choose your amount</h2>
            <p className="section-subtext">
              Select a suggested amount or enter your own donation.
            </p>

            <div className="amount-grid">
              {suggestedAmounts.map((amountOption) => (
                <button
                  key={amountOption}
                  type="button"
                  className={`amount-button ${
                    Number(selectedAmount) === amountOption ? 'selected' : ''
                  }`}
                  onClick={() => handlePresetAmountClick(amountOption)}
                >
                  <span className="amount">{formatCurrency(amountOption)}</span>
                </button>
              ))}

              <div className="custom-amount">
                <div className="dollar-sign">$</div>
                <input
                  type="text"
                  value={formatAmountInput(customAmount)}
                  onChange={handleCustomAmountChange}
                  placeholder="Enter amount"
                  className="custom-amount-input"
                  inputMode="decimal"
                  aria-label="Custom donation amount"
                />
              </div>
            </div>

            <div className="cover-fees-toggle cover-fees-toggle--stacked">
              <label className="cover-fees-label">
                <input
                  type="checkbox"
                  checked={coverFees}
                  onChange={(e) => setCoverFees(e.target.checked)}
                />
                <span>Yes, add 5% to help cover fiscal sponsorship support costs.</span>
              </label>
            </div>

            <div className="mvp-side-card mvp-side-card--inline">
              <h3>Why there is a 5% support option</h3>
              <p>
                This helps support the infrastructure behind professional fiscal
                sponsorship, including compliance, financial oversight, donor receipting,
                and administrative operations.
              </p>
            </div>
          </div>
        </section>
      );
    }

    return (
      <section className="donate-step-panel">
        <div className="donation-review">
          <h2>Review and continue to secure checkout</h2>

          <div className="review-summary">
            <div className="review-item">
              <span className="label">Cause</span>
              <span className="value">{cause.name || cause.projectName}</span>
            </div>

            <div className="review-item">
              <span className="label">Donation amount</span>
              <span className="value">{formatCurrency(donationAmount)}</span>
            </div>

            <div className={`review-item admin-fee ${!coverFees ? 'fee-deducted' : ''}`}>
              <span className="label">Administrative support (5%)</span>
              <span className="value">
                {coverFees ? `+${formatCurrency(adminFee)}` : formatCurrency(0)}
              </span>
            </div>

            <div className="review-item total">
              <span className="label">Total charge</span>
              <span className="value">{formatCurrency(totalAmount)}</span>
            </div>
          </div>

          <div className="secure-checkout-note">
            <p>
              You’ll continue to our secure Salesforce-hosted donation page to complete payment.
            </p>

            <button
              type="button"
              className="step-button step-button--primary"
              onClick={handleContinueToSecureDonation}
              disabled={!isAmountValid}
            >
              Continue to Secure Donation Page <FaExternalLinkAlt />
            </button>

            <a href={`mailto:${CONTACT_EMAIL}`} className="contact-button">
              <FaEnvelope /> Need help with a donation?
            </a>
          </div>

          <div className="legal-footer legal-footer--inline">
            <p>
              {cause.name || cause.projectName} is a fiscally sponsored project of Cause and Impact, Inc. (EIN:
              32-0977089), a Texas nonprofit corporation recognized by the IRS as a
              tax-exempt public charity under Section 501(c)(3) of the Internal Revenue
              Code. All donations are tax-deductible to the extent allowed by law. No goods
              or services were provided in exchange for your contribution.
            </p>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="donate-wrapper">
      <div className="donate-container donate-container--mvp">
        <div className="step-progress step-progress--mvp" aria-label="Donation steps">
          <div className="step-item">
            <div className={`step-badge ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
              1
            </div>
            <span className={`step-text ${step >= 1 ? 'active' : ''}`}>About</span>
          </div>

          <div className={`step-divider ${step >= 2 ? 'active' : ''}`} />

          <div className="step-item">
            <div className={`step-badge ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
              2
            </div>
            <span className={`step-text ${step >= 2 ? 'active' : ''}`}>Amount</span>
          </div>

          <div className={`step-divider ${step >= 3 ? 'active' : ''}`} />

          <div className="step-item">
            <div className={`step-badge ${step >= 3 ? 'active' : ''}`}>3</div>
            <span className={`step-text ${step >= 3 ? 'active' : ''}`}>Checkout</span>
          </div>
        </div>

        <div className="donation-module donation-module--stepper">
          {renderStepContent()}

          <div className="step-actions">
            <div className="step-actions-left">
              {step > 1 && (
                <button type="button" className="step-button step-button--secondary" onClick={goBack}>
                  <FaChevronLeft /> Back
                </button>
              )}
            </div>

            <div className="step-actions-right">
              {step < 3 && (
                <button
                  type="button"
                  className="step-button step-button--primary"
                  onClick={goNext}
                  disabled={step === 2 && !isAmountValid}
                >
                  Next <FaChevronRight />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="donation-back-link-row">
          <Link to="/donate" className="back-link">
            <FaArrowLeft /> Return to Causes
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Donate;